// Ported from packages/desk/src/mobile/layers.ts (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * Layers on the phone home screen (an opened app, the App Library, Search, Profile, a quick-actions menu) and the two
 * things every layer owes the person using it:
 *
 * - The browser's Back closes the top layer before it leaves the page. Each open layer adds one history entry (same
 *   URL, the router's own state kept, so a router that listens to popstate sees nothing change); Back pops it and the
 *   layer closes. A layer closed by its own button takes its entry back off, so the history never fills with dead
 *   entries and Back after that leaves the page as it would have.
 * - Focus moves into the layer when it opens, stays inside it, and goes back where it was when it closes.
 */
import { useEffect, useRef, type RefObject } from "react";

interface Layer {
  token: number;
  close: () => void;
}

const stack: Layer[] = [];
let nextToken = 1;
/**
 * History moves we started ourselves (a layer closed by its own button takes its entry back off with history.go) that
 * have not landed yet. Their popstate is ours, not the person's Back, and is ignored. A layer that opens meanwhile (a
 * quick action closes its menu and opens an app in the same tap) waits to add its entry until the move has landed, or
 * the move would land on top of it and close it. A browser that drops the move is given a moment, then forgotten.
 */
let pending = 0;
let queued: Array<() => void> = [];
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

function settle(): void {
  pending = 0;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
  const run = queued;
  queued = [];
  for (const fn of run) fn();
}

function onPop(): void {
  if (pending > 0) {
    pending -= 1;
    if (pending === 0) settle();
    return;
  }
  // Close every layer whose entry is now ahead of us: one for Back, several for a long press on Back, none for Forward.
  const state: unknown = history.state;
  const here = state && typeof state === "object" && typeof (state as { rdrLayer?: unknown }).rdrLayer === "number" ? (state as { rdrLayer: number }).rdrLayer : 0;
  while (stack.length && stack.at(-1)!.token > here) stack.pop()!.close();
}

function listen(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("popstate", onPop);
}

function addEntry(token: number): void {
  // Still open? A layer can close again before its entry was ever added.
  if (!stack.some((l) => l.token === token)) return;
  try {
    const state: unknown = history.state;
    history.pushState({ ...(state && typeof state === "object" ? state : {}), rdrLayer: token }, "");
  } catch {
    // A sandboxed frame may refuse; the layer still works, Back just leaves the page.
  }
}

/** Adds a history entry for a layer that just opened. Returns its token. */
export function pushLayer(close: () => void): number {
  listen();
  const token = nextToken++;
  stack.push({ token, close });
  if (pending > 0) queued.push(() => addEntry(token));
  else addEntry(token);
  return token;
}

/** A layer closed without Back (its own button, Escape, a swipe): take its entry off the history too. */
export function dropLayer(token: number): void {
  const i = stack.findIndex((l) => l.token === token);
  if (i < 0) return; // already closed by Back
  // Entries above it belong to layers opened after it; they close with it. A layer whose entry is still waiting to be
  // added has none to take off.
  const above = stack.length - i;
  stack.splice(i);
  const waiting = queued.length;
  queued = [];
  const steps = above - Math.min(above, waiting);
  if (steps === 0) return;
  pending += 1;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(settle, 400);
  try {
    history.go(-steps);
  } catch {
    settle();
  }
}

/** For tests: forget every layer. */
export function resetLayers(): void {
  stack.length = 0;
  queued = [];
  settle();
}

export function openLayers(): number {
  return stack.length;
}

/**
 * History moves this module started (a layer closed by its own button) that have not landed yet. Tests wait on it
 * reaching 0 instead of sleeping: a fixed sleep loses the race to the browser's queued traversal on a busy machine.
 */
export function historyMovesPending(): number {
  return pending;
}

/**
 * While `open`, the layer holds a history entry: Back runs `close`. When `open` turns false by any other way, the
 * entry is taken off again.
 */
export function useBackToClose(open: boolean, close: () => void): void {
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    if (!open) return;
    let poppedByBack = false;
    const token = pushLayer(() => {
      poppedByBack = true;
      closeRef.current();
    });
    return () => {
      if (!poppedByBack) dropLayer(token);
    };
  }, [open]);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.closest("[inert]") && el.getAttribute("aria-hidden") !== "true");
}

/**
 * While `active`: focus goes into `ref` (the element marked `data-autofocus`, else the first focusable, else the layer
 * itself), Tab and Shift+Tab stay inside it, and Escape runs `onEscape`. Afterwards focus goes back to what had it.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, onEscape?: () => void): void {
  const esc = useRef(onEscape);
  useEffect(() => {
    esc.current = onEscape;
  });
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = root.querySelector<HTMLElement>("[data-autofocus]") ?? focusables(root)[0] ?? root;
    first.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && esc.current) {
        e.preventDefault();
        e.stopPropagation();
        esc.current();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables(root);
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const at = list.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey ? (at <= 0 ? list.length - 1 : at - 1) : at === list.length - 1 ? 0 : at + 1;
      e.preventDefault();
      list[next]!.focus();
    };
    root.addEventListener("keydown", onKey);
    return () => {
      root.removeEventListener("keydown", onKey);
      if (before?.isConnected) before.focus({ preventScroll: true });
    };
  }, [ref, active]);
}
